'use client'

import ChevronBackward from '@/app/_components/Icon/ChevronBackward'
import ChevronForward from '@/app/_components/Icon/ChevronForward'
import { cn } from '@/app/_primitives/utils/cn'
import { useRecent } from '@/libraries/recent-view'
import { api } from '@/trpc/client'
import { useQuery } from '@tanstack/react-query'
import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useMemo, useRef, useState } from 'react'

export function RecentViewedCharacters() {
  const recent = useRecent()
  const query = useQuery({
    queryKey: ['recent-views', recent.views],
    queryFn: async () => {
      return await api.users.recentlyViewedCharacters.query({
        ids: recent.views,
      })
    },
  })

  if (query.isLoading) return null
  if ((query.data?.length ?? 0) < 5) return null

  return <RecentViews characters={query.data ?? []} />
}

export function RecentViews(props: {
  characters: Array<{
    id: string
    humanReadableID: string
    name: string
    imageURL: string
    colorSpace?: string | null
  }>
}) {
  const characters = props.characters
  const [intersectRatios, setIntersectRatios] = useState<number[]>(() =>
    Array.from({
      length: characters.length,
    }).map(() => 0),
  )

  const [nextFirstIdx, nextLastIdx] = useMemo(() => {
    let firstIntersectIdx = 0
    let lastIntersectIdx = 0

    intersectRatios.forEach((ratio, idx) => {
      if (ratio > 0.5) {
        lastIntersectIdx = idx
      }

      if (ratio <= 0.5) {
        firstIntersectIdx = idx
      }
    })

    intersectRatios.toReversed().forEach((ratio, idx) => {
      if (ratio > 0.5) {
        firstIntersectIdx = intersectRatios.length - 1 - idx
      }
    })

    return [firstIntersectIdx - 1, lastIntersectIdx + 1]
  }, [intersectRatios])

  const scrollToCard = (idx: number) => {
    const card = document.querySelector<HTMLButtonElement>(
      `.recent-view-card-${idx}`,
    )
    if (!card) return

    card.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }

  const onNext = () => {
    if (nextLastIdx >= characters.length) return

    scrollToCard(nextLastIdx)
  }

  const onPrev = () => {
    if (nextFirstIdx < 0) return

    scrollToCard(nextFirstIdx)
  }

  return (
    <div className="flex flex-col gap-12 bg-[#2559AA] pt-27">
      <div className="container">
        <p className="c-h6 text-white lg:c-h5">최근 본 캐릭터</p>
      </div>

      <div className="container relative">
        <button
          onClick={onPrev}
          type="button"
          className={cn(
            'absolute left-0 top-1/2 z-50 translate-y-[calc(-50%_-_37.5px)] outline-none xl:-left-44',
          )}>
          <ChevronBackward width={24} height={24} fill="#fff" />
        </button>
        <button
          onClick={onNext}
          type="button"
          className={cn(
            'absolute right-0 top-1/2 z-50 translate-y-[calc(-50%_-_37.5px)] outline-none xl:-right-44',
          )}>
          <ChevronForward width={24} height={24} fill="#fff" />
        </button>

        <div
          className={cn(
            'recent-views-container no-scrollbar overflow-auto',
            '-mx-10 flex flex-row items-center gap-24 px-10 pb-75',
          )}>
          {characters.map((character, idx) => (
            <RecentViewItem
              key={character.id}
              idx={idx}
              href={`/characters/${character.humanReadableID}`}
              name={character.name}
              imageURL={character.imageURL}
              backgroundColor={character.colorSpace ?? '#FFFFFF'}
              onIntersectChange={(ratio) => {
                setIntersectRatios((prev) => {
                  const newRatios = [...prev]
                  newRatios[idx] = ratio

                  return newRatios
                })
              }}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export function RecentViewItem(props: {
  className?: string
  backgroundColor: string
  imageURL: string
  name: string
  href: string
  idx: number

  onIntersectChange: (ratio: number) => void
}) {
  const divRef = useRef<HTMLAnchorElement>(null)

  useEffect(() => {
    if (!divRef.current) return

    const div = divRef.current
    const intersectionObserver = new IntersectionObserver((entries) => {
      const entry = entries[0]
      if (!entry) return

      props.onIntersectChange(
        entry.isIntersecting ? entry.intersectionRatio : 0,
      )
    })

    intersectionObserver.observe(div)

    return () => intersectionObserver.disconnect()
  }, [props])

  return (
    <Link
      href={props.href}
      ref={divRef}
      className={cn(
        `recent-view-card-${props.idx}`,
        'shadow-web',
        'min-h-242 w-197 shrink-0',
        'overflow-hidden rounded-16',
        'flex flex-col items-stretch',
        'bg-white',
        'mt-10 hover:-mt-10 hover:shadow-xl',
        props.className,
      )}>
      <div
        className="relative h-180"
        style={{ backgroundColor: props.backgroundColor }}>
        <Image
          src={props.imageURL}
          alt={props.name}
          fill
          className="object-cover"
        />
      </div>
      <p className="c-h7 truncate p-20 text-text-base">{props.name}</p>
    </Link>
  )
}
